import { observer } from 'mobx-react';
import notFoundIllustration from '../../../../../assets/error-page/404-illustration.svg';
import { assertNever } from '../../../../../Decoders';
import { T } from '../../../../../Translations';
import { useLearningCollection } from '../../CurrentCollectionStep/BuilderV2/LearningCollectionContext';
import { usePreviewLicense } from './PreviewLicenseContext';

// DVLP-7601: shown in the section slot when the preview has nothing to
// render. Two different reasons, told apart so the admin is not sent to
// licensing for a collection that is simply empty:
//
//   * the collection has no participant-visible content at all (no Builder
//     v2 sections, and either browse-all is off or nothing is attached) —
//     explain that, and point at the browse-all flag when attached courses
//     are the reason;
//   * the selected license unlocks none of it (and "Show locked courses" is
//     off) — mirror the participant Discovery Portal's EmptyLicenseState.

type Reason = 'no-content-browse-all-off' | 'no-content' | 'nothing-unlocked';

function reasonFor(
  totalCount: number,
  browseAll: boolean,
  hasDirectlyAttachedExperiences: boolean,
): Reason {
  if (totalCount > 0) return 'nothing-unlocked';
  if (hasDirectlyAttachedExperiences && !browseAll) return 'no-content-browse-all-off';
  return 'no-content';
}

function Title({ reason }: { reason: Reason }) {
  switch (reason) {
    case 'no-content-browse-all-off':
    case 'no-content':
      return <T kind="Nothing to preview" />;
    case 'nothing-unlocked':
      return <T kind="You're almost there" />;
  }
  return assertNever(reason);
}

function Message({ reason }: { reason: Reason }) {
  switch (reason) {
    case 'no-content-browse-all-off':
      return (
        <T kind="Participants see no courses in this collection. Its attached courses are hidden because Browse all is turned off." />
      );
    case 'no-content':
      return <T kind="Participants see no courses in this collection." />;
    case 'nothing-unlocked':
      return (
        <T kind="This collection requires an active license. Ask your learning administrator to grant you access." />
      );
  }
  return assertNever(reason);
}

function PreviewNoLicensedContent() {
  const { selectedOption } = usePreviewLicense();
  const { resourceState } = useLearningCollection();
  const { browse_all, has_directly_attached_experiences } = resourceState.payload;
  const reason = reasonFor(
    selectedOption.map((o) => o.total_count).getOrElseValue(0),
    browse_all,
    has_directly_attached_experiences,
  );

  return (
    <div
      className="mx-auto xl:container px-6 md:px-14"
      data-intercom-target="preview-no-licensed-content"
      data-preview-empty-reason={reason}
    >
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <img
          src={notFoundIllustration}
          alt=""
          role="presentation"
          className="mb-6 h-auto w-[240px] sm:w-[300px] md:w-[360px]"
          data-intercom-target="preview-no-licensed-illustration"
        />
        <h2
          className="mb-3 text-center md:mb-4"
          style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)' }}
          data-intercom-target="preview-no-licensed-title"
        >
          <Title reason={reason} />
        </h2>
        <p
          className="max-w-[90%] text-center text-muted-foreground md:w-[500px]"
          data-intercom-target="preview-no-licensed-message"
        >
          <Message reason={reason} />
        </p>
      </div>
    </div>
  );
}

export default observer(PreviewNoLicensedContent);
